import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useStudio } from "./StudioContext.js";
import { Section, useYField } from "./form_primitives.js";
import { formLabel as label, sizePresets, resolvePreset, storedSizes, presetLabel, presetMeasurements } from "./techpack_form_contract.js";

export const MEAS_KEY_MAP: Record<string, string> = {
  "front length": "A_front_length",  "back length":  "B_back_length",
  "shoulder":     "C_shoulder",      "bust/chest":   "D_chest",
  "bust":         "D_chest",         "chest":        "D_chest",
  "waist":        "E_waist",         "hip":          "F_hip",
  "hem":          "G_hem",           "sleeve length":"H_sleeve_length",
  "sleeve width": "I_sleeve_width",  "cuff":         "J_cuff",
  "collar width": "K_collar_width",  "waist height": "L_waist_height",
  "front rise":   "M_front_rise",    "back rise":    "N_back_rise",
};
const CUSTOM_MEASUREMENT_KEYS = ["O_inseam", "P_side_seam", "Q_skirt_length_cf", "R_skirt_length_cb", "S_skirt_hem", "T_sleeve_cap"];

export function SizeRangeSection() {
  const { techPack, languageCode: lang, userId } = useStudio();
  const meta = techPack.metadata;
  const [sizeRaw, setSizeRaw] = useYField(meta, "size_range");
  const [system, setSystem] = useYField(meta, "size_system");
  const [gender, setGender] = useYField(meta, "size_gender");
  const [customSizes, setCustomSizes] = useYField(meta, "custom_sizes");
  const [customMeasurements, setCustomMeasurements] = useYField(meta, "custom_measurements");
  const [sample, setSample] = useYField(techPack.measurements, "sample_size");
  const [error, setError] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Language changes must never mutate customer data or silently select a system.
  const preset = resolvePreset(system, gender || "women");
  const activeGender = (preset && preset !== "one_size" ? preset.split("_")[0] : gender) || "women";
  const sizes = customSizes ? storedSizes(customSizes) : Object.keys(sizePresets[preset]?.sizes ?? {});
  const selected = new Set(sizeRaw.split(",").map(s => s.trim()).filter(Boolean));

  function clearCustom() { setCustomSizes(""); setCustomMeasurements(""); setSizeRaw(""); setSample(""); }
  function changeSystem(value: string) { clearCustom(); setSystem(value); }
  function loadMeasurements() {
    let values: Record<string, string> = {};
    if (customSizes) {
      try { values = JSON.parse(customMeasurements)[sample] ?? {}; } catch { setError(true); return; }
    } else values = presetMeasurements(preset, sample);
    techPack.ydoc.transact(() => {
      for (const [key, value] of Object.entries(values))
        techPack.measurements.set(key, { value, language_code: lang, last_edited_by: userId, timestamp: Date.now() });
    });
  }
  async function upload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    setError(false);
    try {
      const book = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const rows = XLSX.utils.sheet_to_json<unknown[]>(book.Sheets[book.SheetNames[0]], { header: 1 });
      const header = rows[0] ?? [];
      // Keep column indexes when a template contains empty columns.
      const columns = header.slice(1).map((v, i) => ({ name: String(v ?? "").trim(), index: i + 1 })).filter(c => c.name);
      if (!columns.length || new Set(columns.map(c => c.name)).size !== columns.length) throw new Error("sizes");
      const map: Record<string, Record<string, string>> = Object.fromEntries(columns.map(c => [c.name, {}]));
      let count = 0;
      for (const row of rows.slice(1)) {
        const name = String(row[0] ?? "").replace(/\s*\(cm\)\s*$/i, "").trim().toLowerCase().replace(/_/g, " ");
        const key = MEAS_KEY_MAP[name]; if (!key) continue;
        for (const col of columns) {
          const value = row[col.index];
          if (value !== undefined && value !== "" && Number.isFinite(Number(value))) { map[col.name][key] = String(value); count++; }
        }
      }
      if (!count) throw new Error("measurements");
      setCustomSizes(JSON.stringify(columns.map(c => c.name)));
      setCustomMeasurements(JSON.stringify(map)); setSizeRaw(""); setSample("");
    } catch { setError(true); }
    e.target.value = "";
  }
  function download() {
    const names = sizes.length ? sizes : ["S", "M", "L"];
    const canonical = Object.entries(MEAS_KEY_MAP).filter(([k]) => !["bust", "chest"].includes(k));
    let values: Record<string, Record<string, string>> = {};
    try { values = customSizes ? JSON.parse(customMeasurements) : Object.fromEntries(names.map(n => [n, presetMeasurements(preset, n)])); } catch { /* empty template */ }
    const data = [["Measurement (cm)", ...names], ...canonical.map(([k, key]) => [k, ...names.map(n => values[n]?.[key] ?? "")])];
    const book = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet(data), "Size Chart");
    XLSX.writeFile(book, "size_chart.xlsx");
  }
  return <Section title={label("size_system", lang)}>
    <div className="sf-grid-2 sf-mb2">
      <label className="sf-label">{label("gender", lang)}
        <select className="sf-select sf-select-compact" value={activeGender === "one" ? "women" : activeGender} onChange={e => { setGender(e.target.value); changeSystem(""); }}>
          {["women", "men", "children"].map(g => <option key={g} value={g}>{label(g, lang)}</option>)}
        </select>
      </label>
      <label className="sf-label">{label("size_system", lang)}
        <select className="sf-select sf-select-compact" value={preset || system} onChange={e => changeSystem(e.target.value)}>
          <option value="">{label("select", lang)}</option>
          {system && !preset && <option value={system}>{system}</option>}
          {Object.keys(sizePresets).filter(k => k.startsWith((activeGender === "one" ? "women" : activeGender) + "_") || k === "one_size").map(k => <option key={k} value={k}>{presetLabel(k, lang)}</option>)}
        </select>
      </label>
    </div>
    <label className="sf-label">{customSizes ? label("custom", lang) + " · " : ""}{label("size_range", lang)}</label>
    <div className="sf-flex-wrap sf-mb2">{sizes.map(sz => <button key={sz} className={`sf-size-btn${selected.has(sz) ? " active" : ""}`} onClick={() => {
      const next = new Set(selected); next.has(sz) ? next.delete(sz) : next.add(sz); setSizeRaw([...next].join(","));
    }}>{sz}</button>)}</div>
    <div className="sf-flex-row sf-mb2">
      <label className="sf-label">{label("sample_size", lang)}
        <select className="sf-select sf-select-compact sf-select-sample" value={sample} onChange={e => setSample(e.target.value)}>
          <option value="">{label("select", lang)}</option>
          {sample && !sizes.includes(sample) && <option value={sample} disabled>{sample}</option>}
          {sizes.map(sz => <option key={sz}>{sz}</option>)}
        </select>
      </label>
      <button className="sf-btn" disabled={!sizes.includes(sample)} onClick={loadMeasurements}>{label("load_sizes", lang)}</button>
    </div>
    <div className="sf-flex-wrap">
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={upload} />
      <button className="sf-btn" onClick={() => fileRef.current?.click()}>{label("upload", lang)}</button>
      <button className="sf-btn" onClick={download}>{label("download", lang)}</button>
      <button className="sf-btn" onClick={() => { clearCustom(); setSystem(""); techPack.ydoc.transact(() => {
        for(const key of [...new Set([...Object.values(MEAS_KEY_MAP), ...CUSTOM_MEASUREMENT_KEYS, "sample_size", "__custom_keys__"])]) techPack.measurements.delete(key);
      }); }}>{label("clear_measurements", lang)}</button>
    </div>
    {error && <p role="alert">{label("invalid_chart", lang)}</p>}
  </Section>;
}
